/**
 * Sheet plein écran qui montre le détail d'une table ouverte (items, totaux,
 * paiements partiels). Alimenté par GET /pos/tables/:n/check.
 *
 * Pas d'action métier en v1 : lecture seule, bouton Fermer.
 * Refresh auto toutes les 15s tant que la sheet est ouverte (pour suivre
 * l'évolution de la commande en cours sans gestion fine de WebSocket).
 */
import React, { useEffect, useRef, useState } from 'react';
import './OpenTableDetailSheet.css';
import { apiFetch } from '../../utils/api';

const REFRESH_INTERVAL_MS = 15_000;

interface CheckItem {
  id: string;
  type: 'product' | 'menu';
  name: string;
  quantity: number;
  unitPriceTTC: number;
  totalTTC: number;
}

interface CheckDetail {
  found: true;
  tableNumber: number;
  receiptId: string;
  sequentialId: number;
  owner: string;
  location: string;
  nbSeats: number;
  openedAt: string;
  duration: string;
  status: 'occupied' | 'paying';
  items: CheckItem[];
  totalTTC: number;
  totalHT: number;
  vat: number;
  paid: { amount: number };
  remaining: number;
}

interface Props {
  tableNumber: number | null;
  onClose: () => void;
}

export function OpenTableDetailSheet({ tableNumber, onClose }: Props) {
  const [detail, setDetail] = useState<CheckDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (tableNumber == null) {
      setDetail(null);
      setError(null);
      return;
    }
    let cancelled = false;
    const load = async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const r = await apiFetch(`/pos/tables/${tableNumber}/check`);
        if (cancelled) return;
        if (r.status === 404) {
          setDetail(null);
          setError("La table vient d'être fermée ou n'existe plus.");
          return;
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as CheckDetail;
        if (!cancelled) {
          setDetail(data);
          setError(null);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message ?? 'Erreur réseau');
      } finally {
        if (!cancelled && !silent) setLoading(false);
      }
    };
    load();
    intervalRef.current = setInterval(() => load(true), REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [tableNumber]);

  if (tableNumber == null) return null;

  return (
    <div className="otd-backdrop" onClick={onClose}>
      <div className="otd-sheet" onClick={(e) => e.stopPropagation()}>
        <button className="otd-close" onClick={onClose} aria-label="Fermer">×</button>

        {loading && !detail && <p className="otd-status">Chargement…</p>}

        {error && !detail && (
          <div className="otd-error">
            <p>{error}</p>
            <button onClick={onClose}>Fermer</button>
          </div>
        )}

        {detail && (
          <>
            {/* Header */}
            <div className="otd-header">
              <div className="otd-header-left">
                <h2>Table {detail.tableNumber}</h2>
                <span className={`otd-badge ${detail.status}`}>
                  {detail.status === 'paying' ? 'En paiement' : 'Occupée'}
                </span>
              </div>
              <div className="otd-header-meta">
                <div>{detail.owner || '—'}</div>
                <div>{detail.nbSeats > 0 ? detail.nbSeats : '—'} couv. · {detail.duration}</div>
              </div>
            </div>

            {/* Items */}
            <div className="otd-section">
              <h3>Commandé ({detail.items.length})</h3>
              {detail.items.length > 0 ? (
                <ul className="otd-items">
                  {detail.items.map((it) => (
                    <li key={it.id} className="otd-item">
                      <div className="otd-item-left">
                        <span className="otd-item-qty">×{it.quantity.toFixed(0)}</span>
                        <span className="otd-item-name">{it.name}</span>
                      </div>
                      <span className="otd-item-total">{it.totalTTC.toFixed(2)} €</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="otd-status">Aucun item commandé</p>
              )}
            </div>

            {/* Totaux */}
            <div className="otd-section otd-totals">
              <div className="otd-line otd-line-muted">
                <span>Total HT</span>
                <span>{detail.totalHT.toFixed(2)} €</span>
              </div>
              <div className="otd-line otd-line-muted">
                <span>TVA</span>
                <span>{detail.vat.toFixed(2)} €</span>
              </div>
              <div className="otd-line otd-line-strong">
                <span>Total TTC</span>
                <span>{detail.totalTTC.toFixed(2)} €</span>
              </div>
            </div>

            {/* Paiements partiels */}
            {detail.paid.amount > 0 && (
              <div className="otd-section otd-paid">
                <div className="otd-line">
                  <span>Encaissé</span>
                  <span>{detail.paid.amount.toFixed(2)} €</span>
                </div>
                <div className="otd-line otd-line-strong">
                  <span>Reste à payer</span>
                  <span>{detail.remaining.toFixed(2)} €</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
